import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant } from "../components/Badge";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, Pencil, X, Smartphone, Monitor, Crop, Maximize2, Focus, Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { fmtDate } from "../lib/format";

const EMPTY = {
  title: "",
  subtitle: "",
  image: "",
  image_mobile: "",
  fit_mode: "cover",
  focal_point: "center",
  bg_color: "",
  link: "",
  order: 0,
  status: "active",
};

const FOCAL_POINTS = [
  { id: "top-left",     label: "↖", title: "Top-left",     pos: "left top" },
  { id: "top",          label: "↑", title: "Top-centre",   pos: "center top" },
  { id: "top-right",    label: "↗", title: "Top-right",    pos: "right top" },
  { id: "left",         label: "←", title: "Centre-left",  pos: "left center" },
  { id: "center",       label: "●", title: "Centre",       pos: "center center" },
  { id: "right",        label: "→", title: "Centre-right", pos: "right center" },
  { id: "bottom-left",  label: "↙", title: "Bottom-left",  pos: "left bottom" },
  { id: "bottom",       label: "↓", title: "Bottom-centre",pos: "center bottom" },
  { id: "bottom-right", label: "↘", title: "Bottom-right", pos: "right bottom" },
];

const focalToPos = (id) => (FOCAL_POINTS.find((f) => f.id === id) || FOCAL_POINTS[4]).pos;

export default function Banners() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/banners");
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      title: row.title || "",
      subtitle: row.subtitle || "",
      image: row.image || "",
      image_mobile: row.image_mobile || "",
      fit_mode: row.fit_mode === "contain" ? "contain" : "cover",
      focal_point: FOCAL_POINTS.find((f) => f.id === row.focal_point) ? row.focal_point : "center",
      bg_color: row.bg_color || "",
      link: row.link || "",
      order: typeof row.order === "number" ? row.order : 0,
      status: row.status || "active",
    });
    setOpen(true);
  };

  const close = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) {
        await api.patch(`/banners/${editing.id}`, payload);
        toast.success("Banner updated");
      } else {
        await api.post("/banners", payload);
        toast.success("Banner created");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editing ? "Update failed" : "Create failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    await api.delete(`/banners/${row.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="banners-page">
      <PageHeader
        title="Banner Management"
        subtitle="Banners auto-crop to fit both desktop & mobile slots. Pick the focal point so the important part of your image stays visible after cropping. Use Contain mode if you'd rather show the full image (with a blurred backdrop)."
        actions={(
          <button onClick={openCreate} className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5" data-testid="banners-add-btn">
            <Plus size={14} /> Add Banner
          </button>
        )}
      />

      {open && (
        <BannerForm
          form={form}
          setForm={setForm}
          onSubmit={submit}
          onClose={close}
          saving={saving}
          editing={!!editing}
        />
      )}

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">No banners yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="banners-grid">
          {rows.map((r) => (
            <BannerCard
              key={r.id}
              row={r}
              onEdit={() => openEdit(r)}
              onDelete={() => remove(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BannerCard({ row, onEdit, onDelete }) {
  const fit = row.fit_mode === "contain" ? "contain" : "cover";
  const fitClass = fit === "cover" ? "object-cover" : "object-contain";
  const focal = row.focal_point || "center";
  const focalPos = focalToPos(focal);
  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden group" data-testid={`banner-card-${row.id}`}>
      <div className="relative h-44 bg-[#0a1119] overflow-hidden">
        {(row.image || row.image_mobile) ? (
          <>
            {fit === "contain" && !row.bg_color && (
              <img src={row.image || row.image_mobile} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60" />
            )}
            <img
              src={row.image || row.image_mobile}
              alt={row.title}
              style={{
                objectPosition: fit === "cover" ? focalPos : "center",
                ...(row.bg_color ? { backgroundColor: row.bg_color } : {}),
              }}
              className={`relative z-[1] w-full h-full ${fitClass} transition-transform duration-300 group-hover:scale-105`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const ph = e.currentTarget.nextElementSibling;
                if (ph) ph.style.display = "flex";
              }}
            />
            <div className="absolute inset-0 z-[1] hidden flex-col items-center justify-center gap-2 text-rose-400 bg-rose-950/40 text-center px-4">
              <AlertTriangle size={22} />
              <div className="text-xs font-semibold leading-tight">Image link broken or blocked</div>
              <div className="text-[10px] text-rose-300 leading-tight">Click Edit → re-upload from your device</div>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-amber-400 bg-amber-950/40">
            <AlertTriangle size={22} />
            <div className="text-xs font-semibold">No image set</div>
            <div className="text-[10px] text-amber-300">Click Edit to add one</div>
          </div>
        )}
        <div className="absolute inset-0 z-[2] bg-gradient-to-t from-black/60 via-black/10 to-transparent flex flex-col justify-end p-4 pointer-events-none">
          <div className="font-display text-xl font-bold text-white">{row.title}</div>
          {row.subtitle && <div className="text-xs text-white/80 mt-0.5">{row.subtitle}</div>}
        </div>
      </div>
      <div className="p-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
          <span className="inline-flex items-center gap-1 h-6 px-1.5 border border-gray-200 rounded-sm text-gray-700 capitalize">
            {fit === "cover" ? <Crop size={10} /> : <Maximize2 size={10} />} {fit}
          </span>
          {fit === "cover" && focal !== "center" && (
            <span className="inline-flex items-center gap-1 h-6 px-1.5 border border-indigo-200 bg-indigo-50 text-indigo-700 rounded-sm" title={`Focal point: ${focal}`}>
              focal · {focal}
            </span>
          )}
          {row.image_mobile && (
            <span className="inline-flex items-center gap-1 h-6 px-1.5 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-sm" title="Has mobile-specific image">
              <Smartphone size={10} /> mobile
            </span>
          )}
          <span>Order · {row.order}</span>
          <span>{fmtDate(row.created_at)}</span>
        </div>
        <div className="flex items-center gap-1">
          {row.link && (
            <a
              href={row.link}
              target="_blank"
              rel="noreferrer"
              title="Open link"
              className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-blue-600 hover:border-blue-200"
            >
              <ExternalLink size={14} />
            </a>
          )}
          <button
            onClick={onEdit}
            data-testid={`banner-edit-${row.id}`}
            className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 rounded-sm text-gray-700 hover:bg-gray-50"
          >
            <Pencil size={12} /> Edit
          </button>
          <button
            onClick={onDelete}
            data-testid={`banner-delete-${row.id}`}
            className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function BannerForm({ form, setForm, onSubmit, onClose, saving, editing }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="font-display font-bold">{editing ? "Edit banner" : "Add banner"}</h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center"><X size={16} /></button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="banners-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Title *"
              value={form.title}
              onChange={(v) => setForm({ ...form, title: v })}
              required
              testId="banners-input-title"
            />
            <Input
              label="Subtitle"
              value={form.subtitle}
              onChange={(v) => setForm({ ...form, subtitle: v })}
              testId="banners-input-subtitle"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ImageInput
              label="Desktop image"
              icon={<Monitor size={11} />}
              value={form.image}
              onChange={(v) => setForm({ ...form, image: v })}
              hint="Recommended: 1920×600 (wide) — or upload a file directly"
              testId="banners-input-image"
            />
            <ImageInput
              label="Mobile image (optional)"
              icon={<Smartphone size={11} />}
              value={form.image_mobile}
              onChange={(v) => setForm({ ...form, image_mobile: v })}
              hint="Mobile (4:3): 1200×900 works best · or 1080×1080 square (slight crop). Leave empty to reuse desktop."
              testId="banners-input-image-mobile"
            />
          </div>

          {/* Fit mode + focal-point + bg colour */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Fit mode</label>
              <div className="grid grid-cols-2 gap-2" data-testid="banners-fit-mode">
                <FitOption
                  active={form.fit_mode !== "contain"}
                  onClick={() => setForm({ ...form, fit_mode: "cover" })}
                  icon={<Crop size={14} />}
                  label="Cover (auto-crop)"
                  hint="Image fills the slot. Pick focal point →"
                  testId="banners-fit-cover"
                />
                <FitOption
                  active={form.fit_mode === "contain"}
                  onClick={() => setForm({ ...form, fit_mode: "contain" })}
                  icon={<Maximize2 size={14} />}
                  label="Contain (full image)"
                  hint="Letter-boxed with blurred backdrop"
                  testId="banners-fit-contain"
                />
              </div>
            </div>

            {/* Focal-point grid (only meaningful in cover mode) */}
            <div className={form.fit_mode === "contain" ? "opacity-50 pointer-events-none" : ""}>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5 inline-flex items-center gap-1">
                <Focus size={11} /> Focal point
                <span className="text-gray-400 font-normal normal-case tracking-normal text-[10px]">— part of image to keep visible after crop</span>
              </label>
              <div className="grid grid-cols-3 gap-1 max-w-[156px]" data-testid="banners-focal-grid">
                {FOCAL_POINTS.map((f) => {
                  const active = form.focal_point === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setForm({ ...form, focal_point: f.id })}
                      title={f.title}
                      data-testid={`banners-focal-${f.id}`}
                      className={`h-12 w-12 inline-flex items-center justify-center rounded-sm border text-base font-bold transition-colors ${
                        active
                          ? "bg-emerald-500 border-emerald-500 text-white shadow"
                          : "bg-white border-gray-200 text-gray-500 hover:border-emerald-300 hover:text-emerald-600"
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-gray-500 mt-1">Tip: if your subject is on the left, pick <b>Centre-left ←</b>.</div>
            </div>
          </div>

          {/* Backdrop colour (only used in contain mode) */}
          {form.fit_mode === "contain" && (
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Backdrop colour (Contain only)</label>
              <div className="flex items-center gap-2 max-w-md">
                <input
                  type="color"
                  value={form.bg_color || "#0a1119"}
                  onChange={(e) => setForm({ ...form, bg_color: e.target.value })}
                  className="h-9 w-12 border border-gray-200 rounded-sm cursor-pointer"
                  data-testid="banners-input-bg-color"
                />
                <input
                  value={form.bg_color}
                  onChange={(e) => setForm({ ...form, bg_color: e.target.value })}
                  placeholder="(empty = auto blurred backdrop)"
                  className="flex-1 h-9 px-3 border border-gray-200 rounded-sm text-sm font-mono"
                />
                {form.bg_color && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, bg_color: "" })}
                    className="h-9 px-2 text-xs text-gray-500 border border-gray-200 rounded-sm hover:text-gray-900"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="text-[10px] text-gray-500 mt-1">Leave empty to use the auto blurred-image backdrop (recommended).</div>
            </div>
          )}

          {/* Link / order / status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="Link URL"
              value={form.link}
              onChange={(v) => setForm({ ...form, link: v })}
              placeholder="https://… (opens on click)"
              testId="banners-input-link"
            />
            <Input
              label="Order"
              type="number"
              value={form.order}
              onChange={(v) => setForm({ ...form, order: v })}
              testId="banners-input-order"
            />
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                data-testid="banners-input-status"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Live preview — desktop + mobile */}
          {form.image && (
            <div className="border-t border-gray-200 pt-4">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Live preview · auto-cropped result</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PreviewBox
                  label="Desktop · 16:5"
                  icon={<Monitor size={11} />}
                  ratioClass="aspect-[16/5]"
                  src={form.image}
                  fit={form.fit_mode}
                  focal={form.focal_point}
                  bgColor={form.bg_color}
                />
                <PreviewBox
                  label="Mobile · 16:9"
                  icon={<Smartphone size={11} />}
                  ratioClass="aspect-[16/9]"
                  src={form.image_mobile || form.image}
                  fit={form.fit_mode}
                  focal={form.focal_point}
                  bgColor={form.bg_color}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-10 px-4 border border-gray-200 rounded-sm text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="banners-submit"
              className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white rounded-sm text-sm font-semibold"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Create banner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FitOption({ active, onClick, icon, label, hint, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-2.5 border rounded-sm transition-colors ${
        active
          ? "border-emerald-400 bg-emerald-50"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className={`flex items-center gap-1.5 text-sm font-semibold ${active ? "text-emerald-700" : "text-gray-700"}`}>
        {icon} {label}
      </div>
      <div className={`text-[10px] mt-0.5 ${active ? "text-emerald-600" : "text-gray-500"}`}>{hint}</div>
    </button>
  );
}

function PreviewBox({ label, icon, ratioClass, src, fit, focal, bgColor }) {
  const isCover = fit === "cover";
  const objectClass = isCover ? "object-cover" : "object-contain object-center";
  const objectPosition = isCover ? focalToPos(focal || "center") : "center";
  return (
    <div>
      <div className="text-[10px] text-gray-500 mb-1 inline-flex items-center gap-1 font-semibold uppercase tracking-wider">{icon} {label}</div>
      <div
        className={`relative ${ratioClass} rounded-sm overflow-hidden bg-[#0a1119]`}
        style={bgColor ? { backgroundColor: bgColor } : undefined}
      >
        {!isCover && !bgColor && (
          <img
            src={src}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <img
          src={src}
          alt="preview"
          className={`relative z-[1] w-full h-full ${objectClass}`}
          style={{ objectPosition }}
          onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
        />
      </div>
    </div>
  );
}

function ImageInput({ label, icon, value, onChange, hint, testId }) {
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadStatus, setLoadStatus] = useState("idle"); // idle | loading | ok | failed

  // Validate URL by trying to load it as an image (catches hotlink + 404 + CORS image-block)
  useEffect(() => {
    if (!value) { setLoadStatus("idle"); return; }
    setLoadStatus("loading");
    const img = new Image();
    let cancelled = false;
    img.onload = () => { if (!cancelled) setLoadStatus("ok"); };
    img.onerror = () => { if (!cancelled) setLoadStatus("failed"); };
    img.src = value.startsWith("/") ? value : value; // browser handles relative
    return () => { cancelled = true; };
  }, [value]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/banners/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Build absolute URL using same backend origin
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const absoluteUrl = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      onChange(absoluteUrl);
      toast.success(`Uploaded (${Math.round(data.size / 1024)} KB)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = ""; // allow re-upload of same file
    }
  };

  const onImportFromUrl = async () => {
    if (!value) return;
    setImporting(true);
    try {
      const { data } = await api.post("/banners/import-url", { url: value });
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const absoluteUrl = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      onChange(absoluteUrl);
      toast.success(`Imported & rehosted (${Math.round(data.size / 1024)} KB · ${data.mime})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not import image from that URL");
    } finally {
      setImporting(false);
    }
  };

  // Detect external URLs that we can attempt to import & rehost
  const isExternalHttp = !!value && /^https?:\/\//i.test(value) && !value.includes("/api/banners/image/");
  const canImport = isExternalHttp && (loadStatus === "failed" || loadStatus === "ok");

  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1 inline-flex items-center gap-1">
        {icon} {label}
      </label>
      <div className="flex items-stretch gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload →"
          className="flex-1 min-w-0 h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
          data-testid={testId}
        />
        <label
          className={`shrink-0 inline-flex items-center justify-center gap-1 h-10 px-3 border rounded-sm text-xs font-semibold cursor-pointer transition-colors ${
            uploading
              ? "border-gray-200 bg-gray-100 text-gray-400 cursor-wait"
              : "border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          }`}
          data-testid={`${testId}-upload`}
        >
          <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
            disabled={uploading}
            onChange={onFile}
            className="hidden"
          />
        </label>
        {canImport && (
          <button
            type="button"
            onClick={onImportFromUrl}
            disabled={importing}
            title="Fetch this image and rehost it on our server (fixes hotlink-protected images)"
            className={`shrink-0 inline-flex items-center justify-center gap-1 h-10 px-3 border rounded-sm text-xs font-semibold transition-colors ${
              importing
                ? "border-gray-200 bg-gray-100 text-gray-400 cursor-wait"
                : "border-indigo-500 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            }`}
            data-testid={`${testId}-import-url`}
          >
            <Upload size={13} /> {importing ? "Importing…" : "Import"}
          </button>
        )}
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            title="Clear"
            className="shrink-0 h-10 w-10 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {/* Status line */}
      <div className="text-[10px] mt-1 flex items-center gap-1.5 flex-wrap">
        {loadStatus === "loading" && <span className="text-gray-500">Checking image…</span>}
        {loadStatus === "ok" && (
          <span className="text-emerald-600 font-semibold inline-flex items-center gap-1">
            <CheckCircle2 size={11} /> Image loads correctly
            {isExternalHttp && (
              <span className="ml-1 text-indigo-600 font-normal">
                · External URL — click <b>Import</b> to rehost on our server (recommended for reliability)
              </span>
            )}
          </span>
        )}
        {loadStatus === "failed" && (
          <span className="text-rose-600 font-semibold inline-flex items-center gap-1">
            <AlertTriangle size={11} /> Cannot load this URL — hotlink-protected, blocked by CORS, or wrong MIME type.
            {isExternalHttp && <span className="ml-1 text-indigo-700 font-normal">Click <b>Import</b> to fetch it server-side, or use <b>Upload</b>.</span>}
          </span>
        )}
        {loadStatus === "idle" && hint && <span className="text-gray-500">{hint}</span>}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, placeholder, hint, icon, testId }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1 inline-flex items-center gap-1">
        {icon} {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
        data-testid={testId}
      />
      {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}
